/**
 * `/about-us/family-of-companies/`: the Ryerson Family of Companies overview. On any other
 * `NEXT_PUBLIC_COMPANY` the page isn't built.
 *
 * The body is, in order: the hero (`components/FamilyOfCompanies/FamilyHeroSection.tsx`, copy in
 * `components/FamilyOfCompanies/FamilyHeroContent.tsx`); one block per company category with a
 * card per company (`components/FamilyOfCompanies/FamilyOfCompaniesRefactor/FoCCategories.tsx`),
 * each card linking to that company's page, `./[slug].tsx`; and the shared Contact Us band
 * (`components/Shared/ContactUsRefactor/ContactUs.tsx`, with its photo from `lg` up and without
 * it below).
 *
 * `getStaticProps` fetches at build time the header and footer data and every category with its
 * companies in all three languages
 * (`src/contentful/family-of-companies/getContentfulFoCCompanyCategoriesAndCompanies.ts`, which
 * rethrows so a Contentful failure fails the build). The meta tags come from
 * `src/content/ryerson/about-us/family-of-companies/index.ts` → `MetaObject`; that module's
 * `PageContent` is the page's site-search entry (`scripts/staticSearch.ts`).
 *
 * The About Us landing page shows the same companies as a logo strip
 * (`components/AboutUs/FamilyOfCompanies/FamilyOfCompanies.tsx`), from its own fetch.
 * `src/unit-test/AboutUs/FamilyOfCompanies.spec.tsx` renders this page with `FoCCategories`
 * mocked, as a smoke test.
 */
import React from "react";
import FamilyHeroSection from "@components/FamilyOfCompanies/FamilyHeroSection";
import { FamilyHeroContent } from "@components/FamilyOfCompanies/FamilyHeroContent";
import { useApplication } from "@ryerson/frontend.application";
import ContactUs from "@components/Shared/ContactUsRefactor/ContactUs";
import { useHasMounted } from "@components/Hooks";
import MetaHead from "@components/Shared/model/MetaHead";
import { MetaObject } from "@content/ryerson/about-us/family-of-companies";
import { Media } from "@ryerson/frontend.next";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import getContentfulFoCCompanyCategoriesAndCompanies from "@contentful/family-of-companies/getContentfulFoCCompanyCategoriesAndCompanies";
import FoCCategories, {
	FoCCategoriesProps,
} from "@components/FamilyOfCompanies/FamilyOfCompaniesRefactor/FoCCategories";

/**
 * Ryerson build only. Fetches the header and footer data and the categories with their
 * companies (`foCCategoriesByLocale`, keyed by language, then by category slug).
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	const { props: headerFooterProps } = await allContentfulHeaderCalls();
	return {
		props: {
			...headerFooterProps,
			foCCategoriesByLocale: await getContentfulFoCCompanyCategoriesAndCompanies(),
		},
	};
};

/**
 * Renders the overview in the current language. Returns `null` until the first client mount,
 * so the exported HTML has no page body.
 */
const Page: React.FC<HeaderPageProps & FoCCategoriesProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	if (!useHasMounted()) return null;

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<FamilyHeroSection content={FamilyHeroContent[language]} />
			<FoCCategories foCCategoriesByLocale={props.foCCategoriesByLocale} />
			<Media greaterThanOrEqual="lg">
				<ContactUs background="primary" withImage={true} oneButtonVariant={true} />
			</Media>
			<Media lessThan="lg">
				<ContactUs background="primary" withImage={false} oneButtonVariant={true} />
			</Media>
		</HeaderLayout>
	);
};

export default Page;
